import { ApplicationLocale, SubsidyProgram } from '@common/entities';

import type { Application, ApplicationChangeHistory, Property } from 'types/application';

import { get, patch, post, put } from '../config';

export const APPLICATION_END_POINTS = '/applications';

export const getApplications = async (clientId: number, folderId?: string) => {
  if (!clientId) throw new Error('Please provide client Id.');

  const queryParams = new URLSearchParams({ clientid: clientId.toString() });
  if (folderId) {
    queryParams.append('folderId', folderId);
  }

  const response = await get<Application[]>(`${APPLICATION_END_POINTS}?${queryParams.toString()}`);
  return response;
};

export interface CompanyIdentificationType {
  id: number;
  system_code?: string;
  name: string;
  property_ids: number[];
  is_published: boolean;
}

export const getCompanyIdentificationTypes = async () => {
  const response = await get<CompanyIdentificationType[]>(
    `${APPLICATION_END_POINTS}/company-identification-types`,
  );
  return response;
};

export const searchApplications = async (clientId: number, search: string) => {
  if (!clientId) throw new Error('Please provide client Id.');
  if (!search || search.trim().length === 0) {
    return { data: [] };
  }

  const queryParams = new URLSearchParams({
    clientid: clientId.toString(),
    search: search.trim(),
  });

  const response = await get<Application[]>(
    `${APPLICATION_END_POINTS}/search?${queryParams.toString()}`,
  );
  return response;
};

type ApplicationResponse = Omit<Application, 'property_associations'>;
export const getApplication = async (applicationId: string, formVersionId: string) => {
  if (!applicationId || !formVersionId) throw new Error('Missing parameter.');

  const response = await get<ApplicationResponse>(
    `${APPLICATION_END_POINTS}/${applicationId}/versions/${formVersionId}`,
  );
  return response;
};

export interface LeasePacket {
  id: number;
  name: string;
  description: string;
}

export interface LeasePacketsByProperty {
  propertyId: number;
  packets: LeasePacket[];
}

export const getLeasePackets = async (
  applicationId: string,
  versionId: string,
): Promise<LeasePacketsByProperty[]> => {
  if (!applicationId || !versionId) {
    throw new Error('Missing application or version parameter.');
  }

  const response = await get<LeasePacketsByProperty[]>(
    `${APPLICATION_END_POINTS}/${applicationId}/versions/${versionId}/lease-packets`,
  );
  return response.data;
};

export interface LeasePacketPreference {
  propertyId: number;
  autoLeaseEnabled: boolean;
  packetId: number | null;
}

export const getLeasePacketPreferences = async (
  applicationId: string,
  versionId: string,
): Promise<LeasePacketPreference[]> => {
  if (!applicationId || !versionId) {
    throw new Error('Missing application or version parameter.');
  }

  const response = await get<LeasePacketPreference[]>(
    `${APPLICATION_END_POINTS}/${applicationId}/versions/${versionId}/lease-packet-preferences`,
  );
  return response.data;
};

export interface SaveLeasePacketRequest {
  companyApplicationId: number;
  autoLeaseEnabled?: boolean;
  packetId: number | null;
}

export interface SaveLeasePacketsRequest {
  companyApplicationId: number;
  autoLeaseEnabled?: boolean;
  properties: Array<{
    propertyId: number;
    packetId: number | null;
  }>;
}

export const saveLeasePacket = async (
  applicationId: string,
  versionId: string,
  propertyId: number,
  request: SaveLeasePacketRequest,
) => {
  if (
    !applicationId ||
    !versionId ||
    !Number.isInteger(propertyId) ||
    propertyId <= 0 ||
    !Number.isInteger(request.companyApplicationId) ||
    request.companyApplicationId <= 0
  ) {
    throw new Error('Missing lease packet save parameters.');
  }

  return put(
    `${APPLICATION_END_POINTS}/${applicationId}/versions/${versionId}/properties/${propertyId}/lease-packet`,
    request,
  );
};

export const saveLeasePackets = async (
  applicationId: string,
  versionId: string,
  request: SaveLeasePacketsRequest,
) => {
  if (
    !applicationId ||
    !versionId ||
    !Number.isInteger(request.companyApplicationId) ||
    request.companyApplicationId <= 0 ||
    !Array.isArray(request.properties) ||
    request.properties.some(
      (property) =>
        !Number.isInteger(property.propertyId) ||
        property.propertyId <= 0 ||
        (property.packetId !== null &&
          (!Number.isInteger(property.packetId) || property.packetId <= 0)),
    )
  ) {
    throw new Error('Missing lease packet save parameters.');
  }
  return put(`${APPLICATION_END_POINTS}/${applicationId}/versions/${versionId}/lease-packets`, {
    ...request,
    autoLeaseEnabled: request.autoLeaseEnabled ?? false,
  });
};

interface CreateApplication {
  name: string;
  description: string;
  occupancyType: string;
  defaultLanguage: string;
  properties?: {
    propertyId: number;
    propertyName: string;
  }[];
  subsidyPrograms?: SubsidyProgram[];
  templateFormId: string;
}
type CreateApplicationResponse = Omit<Application, 'properties' | 'property_associations'>;
export const createApplication = async (data: CreateApplication) => {
  const response = await post<CreateApplicationResponse>(APPLICATION_END_POINTS, data);
  return response;
};

export const getApplicationChanges = async (
  applicationId: string,
  formVersionId: string,
  pageNumber: number,
  pageSize: number,
) => {
  if (!applicationId || !formVersionId || !pageNumber || !pageSize) {
    throw new Error('Missing Parameters.');
  }

  const response = await get<ApplicationChangeHistory[]>(
    `${APPLICATION_END_POINTS}/${applicationId}/versions/${formVersionId}/formchanges/pages/${pageNumber}/${pageSize}`,
  );
  return response;
};

type PatchApplicationResponse = Omit<Application, 'property_associations' | 'updatedBy'>;
export const patchApplication = async (data: any) => {
  const response = await patch<PatchApplicationResponse>(
    `${APPLICATION_END_POINTS}/${data.id}`,
    data,
  );
  return response;
};

type DeleteApplicationResponse = Omit<
  Application,
  'properties' | 'property_associations' | 'updatedBy' | 'versions'
>;
export const deleteApplication = async (applicationId: string) => {
  if (!applicationId) throw new Error('Application Id is missing.');

  const response = await patch<DeleteApplicationResponse>(
    `${APPLICATION_END_POINTS}/soft-delete/${applicationId}`,
  );
  return response;
};

interface PublishVersionResponse {
  id: string;
  applicationId: string;
  formId: string;
  status: string;
  parentVersionId: string;
  versionNumber: number;
  updatedDate: string;
  updatedBy: number;
  publishedDate: string;
}
export const publishVersion = async (data: { applicationId: string; formVersionId: string }) => {
  const { applicationId, formVersionId } = data;
  if (!applicationId || !formVersionId) throw new Error('Missing parameter.');

  const response = await patch<PublishVersionResponse>(
    `${APPLICATION_END_POINTS}/${applicationId}/versions/${formVersionId}/publish`,
    null,
  );
  return response;
};

type DuplicateApplicationReq = {
  applicationId: string;
  name: string;
};
export const duplicateApplication = async (data: DuplicateApplicationReq) => {
  const { applicationId, name } = data;
  if (!applicationId || !name) throw new Error('Missing parameter.');

  const response = await post(`${APPLICATION_END_POINTS}/duplicate-application`, data);
  return response?.data;
};

function triggerJsonDownload(payload: Record<string, unknown>, filename: string): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export const exportApplicationVersion = async (
  applicationId: string,
  versionId: string,
): Promise<void> => {
  if (!applicationId || !versionId) {
    throw new Error('Missing parameter.');
  }

  const response = await get<Record<string, unknown>>(
    `${APPLICATION_END_POINTS}/${applicationId}/versions/${versionId}/export`,
  );
  const payload = response.data;
  const version = payload.version as { versionNumber?: number } | undefined;
  const appName = String(payload.sourceApplicationName || 'application')
    .replace(/[^a-z0-9-_]+/gi, '_')
    .slice(0, 80);
  const versionSuffix = version?.versionNumber != null ? `-v${version.versionNumber}` : '';
  triggerJsonDownload(payload, `${appName}${versionSuffix}-application-export.json`);
};

export type ImportFormDefinitionRequest = {
  name: string;
  description?: string;
  occupancyType?: string;
  folderId?: string | null;
  formExport: Record<string, unknown>;
  properties?: Property[];
  subsidyPrograms?: SubsidyProgram[];
};

export const importFormDefinition = async (data: ImportFormDefinitionRequest) => {
  const { name, formExport } = data;
  if (!name?.trim() || !formExport) {
    throw new Error('Missing parameter.');
  }

  const response = await post<Application>(
    `${APPLICATION_END_POINTS}/import-form-definition`,
    data,
  );
  return response?.data;
};

export const getApplicationLocales = async (applicationId: string) => {
  if (!applicationId) throw new Error('Application Id is required.');

  const response = await get<ApplicationLocale[]>(
    `${APPLICATION_END_POINTS}/${applicationId}/locales`,
  );
  return response;
};

interface UpdateApplicationSettingProps {
  applicationId: string;
  formVersionId: string;
  settings: {
    primary?: { minAge?: number };
    coapplicant?: { minAge?: number };
    guarantor?: { minAge?: number };
    promoCode?: {
      enabled?: boolean;
      descriptiveText?: string;
    };
  };
}
export const updateApplicationSetting = async (data: UpdateApplicationSettingProps) => {
  const { applicationId, formVersionId, settings } = data;

  if (!applicationId || !formVersionId) {
    throw new Error(
      'Failed to update application settings: applicationId or formVersionId is required',
    );
  }

  if (!settings || Object.keys(settings).length === 0) {
    throw new Error('Failed to update application settings: No settings provided to update');
  }

  const response = await patch(
    `${APPLICATION_END_POINTS}/${applicationId}/versions/${formVersionId}/settings`,
    settings,
  );
  return response?.data;
};
